import React from 'react';
import styled from 'styled-components';
import { RSSItem } from '../types/rss';

interface NewsListItemProps {
  item: RSSItem;
  showImage: boolean;
  isHighlighted?: boolean;
}

const ListItem = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isHighlighted'
})<{ isHighlighted?: boolean }>`
  display: flex;
  background-color: ${props => props.isHighlighted ? '#dc3545' : (props.theme === 'dark' ? 'var(--background-dark)' : 'white')};
  border-radius: 8px;
  box-shadow: var(--card-shadow);
  overflow: hidden;
  transition: var(--transition);
  border: ${props => props.isHighlighted ? '3px solid #dc3545' : 'none'};
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }
  
  body.dark-theme & {
    background-color: ${props => props.isHighlighted ? '#dc3545' : 'var(--background-dark)'};
    color: ${props => props.isHighlighted ? 'white' : 'var(--text-dark)'};
  }
  
  body.custom-theme & {
    background-color: ${props => props.isHighlighted ? '#dc3545' : 'var(--custom-background-color)'};
    color: ${props => props.isHighlighted ? 'white' : 'var(--custom-font-color)'};
  }
`;

const ItemImage = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'imageUrl'
})<{ imageUrl?: string }>`
  width: 120px;
  min-width: 120px;
  background-image: ${props => props.imageUrl ? `url(${props.imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  background-color: ${props => !props.imageUrl ? 'var(--primary-color)' : 'transparent'};
`;

const ItemContent = styled.div`
  padding: 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const ItemTitle = styled.h3`
  font-size: 18px;
  margin-bottom: 8px;
  line-height: 1.3;
  
  body.dark-theme & {
    color: var(--text-dark);
  }
  
  body.custom-theme & {
    color: var(--custom-font-color);
    font-size: var(--custom-font-size);
  }
`;

const ItemDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
  flex-grow: 1;
  
  body.dark-theme & {
    color: #ccc;
  }
  
  body.custom-theme & {
    color: var(--custom-font-color);
    font-size: calc(var(--custom-font-size) * 0.875);
  }
`;

const ItemFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #999;
  
  body.dark-theme & {
    color: #aaa;
  }
`;

const ItemLink = styled.a`
  display: inline-block;
  color: var(--primary-color);
  font-weight: 500;
  
  &:hover {
    color: var(--secondary-color);
  }
`;

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Função truncateText removida para mostrar texto completo

export const NewsListItem: React.FC<NewsListItemProps> = ({ item, showImage, isHighlighted = false }) => {
  return (
    <ListItem className="news-item" isHighlighted={isHighlighted}>
      {showImage && item.imageUrl && <ItemImage imageUrl={item.imageUrl} />}
      <ItemContent>
        <ItemTitle>{item.title}</ItemTitle>
        <ItemDescription>{item.description}</ItemDescription>
        <ItemFooter>
          <span>{formatDate(item.pubDate)}</span>
          <ItemLink href={item.link} target="_blank" rel="noopener noreferrer">
            Leia mais
          </ItemLink>
        </ItemFooter>
      </ItemContent>
    </ListItem>
  );
};